import {
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import type { AuthUser } from '../../core/types/auth.types';
import { AuthService } from '../../core/services/auth.service';
import { UserApiService } from '../../core/services/user-api.service';

/** Normaliza para comparar identidades (handle/e-mail vêm com caixa e `@` variáveis). */
function idKey(value: string | null | undefined): string {
  return (value ?? '').replace(/^@+/u, '').trim().toLowerCase();
}

@Component({
  selector: 'app-people-search-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './people-search-bar.component.html',
  styleUrl: './people-search-bar.component.scss',
})
export class PeopleSearchBarComponent {
  private readonly userApi = inject(UserApiService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly query$ = new Subject<string>();

  /** Quando true, o campo ocupa a largura disponível (ex.: landing); senão mantém 260px como na navbar. */
  readonly stretch = input(false);

  /** Texto do placeholder e do `aria-label` do campo. */
  readonly searchPlaceholder = input('Encontrar pessoas');

  readonly searchRoot = viewChild<ElementRef<HTMLElement>>('searchRoot');
  readonly searchField = viewChild<ElementRef<HTMLInputElement>>('searchField');

  readonly searchResults = signal<AuthUser[]>([]);
  readonly searchLoading = signal(false);
  readonly searchError = signal(false);
  readonly searchQuery = signal('');
  readonly searchInputFocused = signal(false);

  constructor() {
    this.query$
      .pipe(
        debounceTime(320),
        distinctUntilChanged(),
        switchMap((raw) => {
          const q = raw.trim();
          if (q.length < 2) {
            return of({ users: [] as AuthUser[], err: false, short: true });
          }
          this.searchLoading.set(true);
          this.searchError.set(false);
          return this.userApi.searchUsers(q).pipe(
            map((users) => ({ users: this.withoutCurrentUser(users), err: false, short: false })),
            catchError(() => of({ users: [] as AuthUser[], err: true, short: false })),
            tap(() => this.searchLoading.set(false)),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ users, err, short }) => {
        if (short) {
          this.searchResults.set([]);
          this.searchLoading.set(false);
          return;
        }
        this.searchResults.set(users);
        this.searchError.set(err);
      });
  }

  /**
   * Remove o próprio utilizador dos resultados — a busca é para encontrar
   * outras pessoas, e a API `/User/search` devolve o autor da chamada também.
   *
   * Compara por `externalId` quando disponível; o endpoint de busca não devolve
   * sempre esse campo, então cai para username e e-mail (sem caixa nem `@`).
   */
  private withoutCurrentUser(users: AuthUser[]): AuthUser[] {
    const me = this.authService.currentUser();
    if (!me) return users;

    const myId = idKey(me.externalId);
    const myUsername = idKey(me.username);
    const myEmail = idKey(me.email);

    return users.filter((u) => {
      if (myId && idKey(u.externalId) === myId) return false;
      if (myUsername && idKey(u.username) === myUsername) return false;
      if (myEmail && idKey(u.email) === myEmail) return false;
      return true;
    });
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.query$.next(value);
  }

  onSearchFocus(): void {
    this.searchInputFocused.set(true);
  }

  onSearchBlur(): void {
    setTimeout(() => this.searchInputFocused.set(false), 200);
  }

  showSearchDropdown(): boolean {
    return this.searchInputFocused() && this.searchQuery().trim().length >= 2;
  }

  pickUser(user: AuthUser): void {
    const el = this.searchField()?.nativeElement;
    if (el) {
      el.value = '';
    }
    this.searchQuery.set('');
    this.searchResults.set([]);
    this.searchError.set(false);
    this.query$.next('');
    this.searchInputFocused.set(false);

    const slug = user.username?.replace(/^@+/u, '').trim();
    if (!slug) {
      return;
    }
    void this.router.navigate(['/user', slug, 'wishlists'], { state: { profileUser: user } });
  }

  displayName(u: AuthUser): string {
    return u.fullName?.trim() || u.username || u.email || 'Usuário';
  }

  handleLabel(u: AuthUser): string {
    const un = u.username?.replace(/^@+/u, '').trim();
    return un ? `@${un}` : u.email || '';
  }

  showNoResults(): boolean {
    return (
      !this.searchLoading() &&
      !this.searchError() &&
      this.searchResults().length === 0 &&
      this.searchQuery().trim().length >= 2
    );
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    const t = event.target as Node;
    const root = this.searchRoot()?.nativeElement;
    if (root && !root.contains(t)) {
      this.searchField()?.nativeElement?.blur();
    }
  }
}
